import { useEffect, useRef, useState } from 'react';

type Options = IntersectionObserverInit & {
    // When true (default), the observer disconnects after the first
    // intersection and `inView` stays true forever — the right semantics
    // for one-shot reveal animations. When false, `inView` tracks live
    // visibility (useful for pausing autoplay offscreen). The third tuple
    // element `hasBeenInView` is always sticky.
    once?: boolean;
};

const DEFAULT_THRESHOLD = 0.2;
const DEFAULT_ROOT_MARGIN = '0px 0px -10% 0px';

function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useInView<T extends Element>(options: Options = {}) {
    const { once = true, threshold = DEFAULT_THRESHOLD, rootMargin = DEFAULT_ROOT_MARGIN, root = null } = options;
    const ref = useRef<T | null>(null);
    const [inView, setInView] = useState(() => {
        // Reduced-motion in once mode: skip the observer entirely so reveal
        // animations don't gate on intersection. In continuous mode, callers
        // typically don't want this shortcut (e.g. autoplay should still pause
        // offscreen even under reduce-motion).
        return once && prefersReducedMotion();
    });
    const [hasBeenInView, setHasBeenInView] = useState(inView);

    useEffect(() => {
        if (once && inView) { return; }
        const el = ref.current;
        if (!el) { return; }
        const observer = new IntersectionObserver((entries) => {
            const isIntersecting = entries.some((entry) => entry.isIntersecting);
            if (once) {
                if (isIntersecting) {
                    setInView(true);
                    setHasBeenInView(true);
                    observer.disconnect();
                }
            } else {
                setInView(isIntersecting);
                if (isIntersecting) { setHasBeenInView(true); }
            }
        }, { threshold, rootMargin, root });
        observer.observe(el);
        return () => observer.disconnect();
    }, [inView, once, threshold, rootMargin, root]);

    return [ref, inView, hasBeenInView] as const;
}
